import { PaymentModuleId } from '@foundation/apis/payments';
import { useMemo, useState } from 'react';

import { PaymentsLayout } from '@/components/common';
import { RadioGroup } from '@/components/common/forms/inputs/RadioGroup';
import {
  BraintreeCardForm,
  PayWithBraintreePayPalBtn,
  PayWithBraintreePayPalPayLaterBtn,
} from '@/components/payment-providers/braintree';
import { BraintreeSepaForm } from '@/components/payment-providers/braintree/BraintreeSepaForm';
import { PayWithBraintreeApplePay } from '@/components/payment-providers/braintree/PayWithBraintreeApplePay';
import { PayWithBraintreeGooglePayBtn } from '@/components/payment-providers/braintree/PayWithBraintreeGooglePayBtn';
import { PayWithDenizBankBtn } from '@/components/payment-providers/denizbank/PayWithDenizBankBtn';
import { PayViaECommerceConnectBtn } from '@/components/payment-providers/ecommerceconnect';
import { PayWithFlutterwaveCardBtn } from '@/components/payment-providers/flutterwave/PayWithFlutterwaveCardBtn';
import { LsvForm } from '@/components/payment-providers/lsv';
import { NuveiCardForm } from '@/components/payment-providers/nuvei/NuveiCardForm';
import { PayWithNuveiTwintBtn } from '@/components/payment-providers/nuvei/PayWithNuveiTwintBtn';
import {
  PayByInvoiceBtn,
  PayInSubsidiaryBtn,
  PayWithBankTransferBtn,
} from '@/components/payment-providers/offline';
import { PayInSubsidiaryCashOnPickupBtn } from '@/components/payment-providers/offline/PayInSubsidiaryCashOnPickupBtn';
import { PaymentInSubsidiaryCardViaTerminalOnPickupBtn } from '@/components/payment-providers/offline/PaymentInSubsidiaryCardViaTerminalOnPickupBtn';
import { PayOnDeliveryBtn } from '@/components/payment-providers/offline/PayOnDeliveryBtn';
import { PayWithFinvoiceBtn } from '@/components/payment-providers/offline/PayWithFinvoiceBtn';
import { PostFinanceForm } from '@/components/payment-providers/postfinance';
import { PayWithProcessingKZBtn } from '@/components/payment-providers/processingkz';
import { RazorPayBtn } from '@/components/payment-providers/razorpay';
import { SepaForm } from '@/components/payment-providers/sepa';
import { PayViaTransbankBtn } from '@/components/payment-providers/transbank';
import { PayWithTwoCTwoPBtn } from '@/components/payment-providers/twoctwop';
import {
  PayWithWorldpayBancontactBtn,
  PayWithWorldpayCardBtn,
  PayWithWorldpayIdealBtn,
  PayWithWorldpayJCBBtn,
  PayWithWorldpayKlarnaBtn,
} from '@/components/payment-providers/worldpay';
import { PayWithYookassaBtn } from '@/components/payment-providers/yookassa';
import { PaymentIntentFlow } from '@/schemas/PaymentIntent';
import PaymentStore from '@/services/payments/PaymentStore';

import { computeRadioOptions } from './utils/helper';
import { NavigateToStoredInstrumentsPageBtn } from './utils/NavigateToStoredInstrumentsPageBtn';
import { CancelPaymentButton } from '../stored-instruments/utils/CancelPaymentButton';

interface IPaymentMethodsPageProps {
  readonly flow: PaymentIntentFlow;
}

export function PaymentMethodsPage({ flow }: IPaymentMethodsPageProps) {
  const [methodToRender, setMethodToRender] = useState<PaymentModuleId>();

  //This means user landed on /manage from 'nowhere' (e.g., email) and has no stored instruments
  const isEmptyListForUndefinedCountry =
    flow === PaymentIntentFlow.Manage &&
    PaymentStore.instruments.length === 0 &&
    PaymentStore.legalEntity === undefined;

  const navigation = useMemo(
    () =>
      flow === PaymentIntentFlow.Moto ||
      isEmptyListForUndefinedCountry ||
      !PaymentStore.instruments.length ? null : (
        <NavigateToStoredInstrumentsPageBtn />
      ),
    [flow, isEmptyListForUndefinedCountry],
  );

  const methodComponent = useMemo(() => {
    switch (methodToRender) {
      case PaymentModuleId.BraintreeApplePay:
        return <PayWithBraintreeApplePay navigation={navigation} />;
      case PaymentModuleId.BraintreeCard:
        return <BraintreeCardForm navigation={navigation} />;
      case PaymentModuleId.BraintreeGooglePay:
        return <PayWithBraintreeGooglePayBtn navigation={navigation} />;
      case PaymentModuleId.BraintreePayPal:
        return <PayWithBraintreePayPalBtn navigation={navigation} />;
      case PaymentModuleId.BraintreePayPalPayLater:
        return <PayWithBraintreePayPalPayLaterBtn navigation={navigation} />;
      case PaymentModuleId.PaymentAtSubsidiary:
        return <PayInSubsidiaryBtn navigation={navigation} />;
      case PaymentModuleId.BankTransfer:
        return <PayWithBankTransferBtn navigation={navigation} />;
      case PaymentModuleId.Finvoice:
        return <PayWithFinvoiceBtn navigation={navigation} />;
      case PaymentModuleId.Transbank:
        return <PayViaTransbankBtn navigation={navigation} />;
      case PaymentModuleId.WorldpayBancontact:
        return <PayWithWorldpayBancontactBtn navigation={navigation} />;
      case PaymentModuleId.WorldpayCard:
        return <PayWithWorldpayCardBtn navigation={navigation} />;
      case PaymentModuleId.WorldpayIdeal:
        return <PayWithWorldpayIdealBtn navigation={navigation} />;
      case PaymentModuleId.WorldpayJCB:
        return <PayWithWorldpayJCBBtn navigation={navigation} />;
      case PaymentModuleId.WorldpayKlarna:
        return <PayWithWorldpayKlarnaBtn navigation={navigation} />;
      case PaymentModuleId.ECommerceConnect:
        return <PayViaECommerceConnectBtn navigation={navigation} />;
      case PaymentModuleId.NuveiTwint:
        return <PayWithNuveiTwintBtn navigation={navigation} />;
      case PaymentModuleId.FlutterwaveCreditCard:
        return <PayWithFlutterwaveCardBtn navigation={navigation} />;
      case PaymentModuleId.NuveiCreditCard:
        return <NuveiCardForm navigation={navigation} />;
      case PaymentModuleId.BraintreeSepa:
        return <BraintreeSepaForm navigation={navigation} />;
      case PaymentModuleId.PaymentOnDelivery:
        return <PayOnDeliveryBtn navigation={navigation} />;
      case PaymentModuleId.TwoCTwoP:
        return <PayWithTwoCTwoPBtn navigation={navigation} />;
      case PaymentModuleId.ProcessingKZ:
        return <PayWithProcessingKZBtn navigation={navigation} />;
      case PaymentModuleId.Sepa:
        return <SepaForm navigation={navigation} />;
      case PaymentModuleId.Yookassa:
        return <PayWithYookassaBtn navigation={navigation} />;
      case PaymentModuleId.RazorPay:
        return <RazorPayBtn navigation={navigation} />;
      case PaymentModuleId.DirectDebitLSV:
        return <LsvForm navigation={navigation} />;
      case PaymentModuleId.DirectDebitPostFinance:
        return <PostFinanceForm navigation={navigation} />;
      case PaymentModuleId.DenizBank:
        return <PayWithDenizBankBtn navigation={navigation} />;
      case PaymentModuleId.PaymentInSubsidiaryCashOnPickup:
        return <PayInSubsidiaryCashOnPickupBtn navigation={navigation} />;
      case PaymentModuleId.PaymentInSubsidiaryCardViaTerminalOnPickup:
        return (
          <PaymentInSubsidiaryCardViaTerminalOnPickupBtn
            navigation={navigation}
          />
        );
      case PaymentModuleId.PayByInvoice:
        return <PayByInvoiceBtn navigation={navigation} />;
    }
  }, [methodToRender, navigation]);

  const radioOptions = useMemo(
    () => computeRadioOptions(PaymentStore.allowedPaymentModules),
    [],
  );

  return (
    <PaymentsLayout
      flow={flow}
      footer={<CancelPaymentButton className="!text-black" />}
      navigation={null}
      mainAction={null}
    >
      <RadioGroup
        preSelectedValue={PaymentStore.preSelectedModule?.toString()}
        name="payment-methods"
        options={radioOptions ?? []}
        onSelectionChange={(moduleIdString) =>
          setMethodToRender(Number(moduleIdString))
        }
      />
      <div className="mt-md">{methodComponent}</div>
    </PaymentsLayout>
  );
}
